import { useState, useEffect, useMemo } from 'react';
import type { AgentParameterDto } from '../../types/agent';
import { ParameterInput } from './ParameterInput';

interface ParameterFormProps {
  parameters: AgentParameterDto[];
  values: Record<string, any>;
  onChange: (values: Record<string, any>) => void;
  errors?: Record<string, string>;
}

/**
 * A section of the run form: parameters sharing the same dot-path group
 * prefix ('' for top-level fields). Values are still keyed by the full
 * dot-path key so the submitted dictionary stays flat.
 */
interface ParameterGroup {
  /** Dot-path prefix without the leaf segment ('' for top-level). */
  prefix: string;
  parameters: AgentParameterDto[];
}

/** Groups parameters by their dot-path parent prefix, preserving order. */
const groupParameters = (parameters: AgentParameterDto[]): ParameterGroup[] => {
  const groups: ParameterGroup[] = [];
  for (const param of parameters) {
    const lastDot = param.key.lastIndexOf('.');
    const prefix = lastDot >= 0 ? param.key.substring(0, lastDot) : '';
    const group = groups.find((g) => g.prefix === prefix);
    if (group) {
      group.parameters.push(param);
    } else {
      groups.push({ prefix, parameters: [param] });
    }
  }
  return groups;
};

/**
 * Dynamic form component for agent parameters. Nested input models
 * (dot-path keys like "patient.age") render as grouped sections; the
 * emitted values dictionary stays flat, keyed by the full dot-path.
 */
export const ParameterForm = ({ parameters, values, onChange, errors }: ParameterFormProps) => {
  const [localValues, setLocalValues] = useState<Record<string, any>>(values);

  useEffect(() => {
    setLocalValues(values);
  }, [values]);

  const groups = useMemo(() => groupParameters(parameters), [parameters]);

  const handleParameterChange = (key: string, value: any) => {
    const newValues = { ...localValues, [key]: value };
    setLocalValues(newValues);
    onChange(newValues);
  };

  if (parameters.length === 0) {
    return (
      <div className="rounded-md border border-blue-300 bg-blue-50 px-4 py-3 text-sm text-blue-800 dark:border-blue-700 dark:bg-blue-950 dark:text-blue-300">
        This agent does not require any parameters.
      </div>
    );
  }

  return (
    <div>
      <h6 className="text-lg font-semibold mb-2">
        Agent Parameters
      </h6>
      <p className="text-sm text-muted-foreground mb-4">
        Configure parameters for this agent session
      </p>
      {groups.map((group) => (
        <div key={group.prefix || '(root)'} className={group.prefix ? 'mb-4' : ''}>
          {group.prefix && (
            <span className="text-sm font-semibold mb-2 block font-mono">
              {group.prefix}
            </span>
          )}
          <div className={group.prefix ? 'pl-6' : ''}>
            {group.parameters.map((param) => (
              <ParameterInput
                key={param.key}
                parameter={param}
                value={localValues[param.key]}
                onChange={(value) => handleParameterChange(param.key, value)}
                error={errors?.[param.key]}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
